import { motion } from 'motion/react';

import { cn } from '@shared/lib/utils';

interface LandingVisionProgressIndicatorProps {
  className?: string;
  progresses: readonly number[];
  showThreshold: number;
  exitThreshold: number;
  states: readonly ('hidden' | 'show' | 'exit')[];
}

const getVisionIndicatorProgress = (
  progress: number,
  showThreshold: number,
  exitThreshold: number
) => {
  const normalizedProgress = (progress - showThreshold) / (exitThreshold - showThreshold);

  return Math.min(1, Math.max(0, normalizedProgress));
};

export const LandingVisionProgressIndicator = ({
  className,
  progresses,
  showThreshold,
  exitThreshold,
  states,
}: LandingVisionProgressIndicatorProps) => {
  return (
    <div
      className={cn('pointer-events-none absolute flex flex-col gap-3', className)}
      aria-hidden="true"
    >
      {states.map((state, index) => {
        const indicatorProgress = getVisionIndicatorProgress(
          progresses[index] ?? 0,
          showThreshold,
          exitThreshold
        );

        const indicatorWidth = 10;
        const maxIndicatorHeight = 64;
        const hasIndicatorStarted = indicatorProgress > 0;
        const isActive = state === 'show' && (index === 0 ? hasIndicatorStarted : true);
        const fillOffsetY = indicatorProgress * (maxIndicatorHeight - indicatorWidth);

        return (
          <motion.div
            key={`vision-indicator-${index.toString()}`}
            className="relative overflow-hidden rounded-full bg-background-5"
            style={{ width: indicatorWidth }}
            layout
            animate={{
              height: isActive ? maxIndicatorHeight : indicatorWidth,
            }}
            initial={false}
            transition={{
              duration: 1,
              ease: [0.2, 1, 0.4, 1],
            }}
          >
            <motion.div
              className="absolute top-0 left-0 rounded-full bg-primary"
              style={{
                width: indicatorWidth,
                height: indicatorWidth,
              }}
              animate={{
                opacity: isActive ? 1 : 0,
                y: isActive ? fillOffsetY : fillOffsetY / 2,
              }}
              initial={false}
              transition={{
                y: {
                  duration: isActive ? 0 : 0.2,
                  ease: isActive ? 'linear' : [0.6, 0, 1, 1],
                },
                opacity: {
                  duration: isActive ? 1 : 0.2,
                  ease: isActive ? [0.2, 1, 0.4, 1] : [0.6, 0, 1, 1],
                },
              }}
            />
          </motion.div>
        );
      })}
    </div>
  );
};
